import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/shell/PageHeader";
import { Panel, TableSkeleton } from "@/components/common/primitives";
import { api, unwrapEnvelope } from "@/lib/api";
import { US_ZIP_RE } from "@/lib/settings/constants";
import { useSession } from "@/state/session";

export default function ProfilePage() {
  const { session } = useSession();
  const [bundle, setBundle] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    default_sourcing_postal_code: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .accountProfile()
      .then((res) => {
        const data = unwrapEnvelope(res);
        setBundle(data);
        setForm({
          full_name: data?.profile?.full_name || "",
          phone: data?.profile?.phone || "",
          default_sourcing_postal_code:
            data?.profile?.default_sourcing_postal_code || "",
        });
      })
      .catch(() => toast.error("Could not load profile"));
  }, []);

  async function save() {
    if (
      form.default_sourcing_postal_code &&
      !US_ZIP_RE.test(form.default_sourcing_postal_code.trim())
    ) {
      toast.error("Enter a valid US ZIP code");
      return;
    }
    setSaving(true);
    try {
      const res = await api.patchAccountProfile(form);
      const data = unwrapEnvelope(res);
      setBundle(data);
      toast.success("Profile updated");
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  }

  function copyAccountId() {
    const id = bundle?.profile?.public_account_id;
    if (!id) return;
    navigator.clipboard?.writeText(id);
    toast.success("Account ID copied");
  }

  if (!bundle) return <TableSkeleton rows={6} />;

  const profile = bundle.profile || {};
  const workspace = bundle.workspace || {};

  return (
    <div className="space-y-4" data-testid="profile-page">
      <PageHeader
        title="Profile"
        description="Account details, workspace context, and security."
      />

      <div className="grid lg:grid-cols-2 gap-3">
        <Section title="Account details">
          <Field label="Full name">
            <input
              className="w-full h-9 px-3 rounded-ctrl border border-edge-subtle text-compact"
              value={form.full_name}
              onChange={(e) =>
                setForm((f) => ({ ...f, full_name: e.target.value }))
              }
            />
          </Field>
          <Field label="Email">
            <input
              disabled
              className="w-full h-9 px-3 rounded-ctrl border border-edge-subtle bg-surface-2 text-compact"
              value={profile.email || session?.email || ""}
            />
          </Field>
          <Field label="Phone number">
            <input
              className="w-full h-9 px-3 rounded-ctrl border border-edge-subtle text-compact"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </Field>
          <Field label="Default sourcing ZIP">
            <input
              className="w-full h-9 px-3 rounded-ctrl border border-edge-subtle font-mono text-compact"
              value={form.default_sourcing_postal_code}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  default_sourcing_postal_code: e.target.value,
                }))
              }
            />
          </Field>
          <Field label="Account ID">
            <div className="flex gap-2">
              <input
                readOnly
                className="flex-1 h-9 px-3 rounded-ctrl border border-edge-subtle font-mono text-compact bg-surface-2"
                value={profile.public_account_id || ""}
              />
              <button
                type="button"
                className="h-9 px-3 rounded-ctrl border border-edge-subtle text-compact"
                onClick={copyAccountId}
              >
                Copy
              </button>
            </div>
          </Field>
          <button
            type="button"
            className="h-9 px-3 rounded-ctrl bg-signal-blue text-white text-compact disabled:opacity-50"
            disabled={saving}
            onClick={save}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </Section>

        <div className="space-y-3">
          <Section title="Workspace">
            <StaticField label="Workspace name" value={workspace.name} />
            <StaticField label="Role" value={workspace.role} />
            <StaticField label="Current plan" value={workspace.plan_name} />
          </Section>

          <Section title="Account">
            <StaticField label="Account ID" value={profile.public_account_id} />
            <StaticField label="Member since" value={profile.member_since} />
            <StaticField label="Status" value={profile.status} />
          </Section>

          <Section title="Security">
            <p className="text-compact text-ink-muted">
              Password changes and active device management are available under
              Settings → Devices.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <Panel className="p-4 space-y-3">
      <h2 className="text-stitle text-ink-primary">{title}</h2>
      {children}
    </Panel>
  );
}

function Field({ label, children }) {
  return (
    <label className="block text-compact">
      <span className="text-ink-secondary">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function StaticField({ label, value }) {
  return (
    <div className="text-compact">
      <div className="text-ink-muted text-micro uppercase">{label}</div>
      <div className="text-ink-primary mt-0.5">{value || "—"}</div>
    </div>
  );
}
